import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faLightbulb,
    faRotateRight,
    faPlus,
    faEdit,
    faChevronDown,
    faChevronUp,
    faExclamationTriangle,
    faSpinner,
    faEye,
    faEyeSlash,
    faCog,
    faChartLine,
    faUsers,
    faTags,
    faInfoCircle,
    faHammer
} from '@fortawesome/free-solid-svg-icons';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';
import { innovationRadarService } from '../../services/innovationRadarService';
import LoadingSpinner from '../Common/LoadingSpinner';
import InnovationRadarEditModal from './InnovationRadarEditModal';
import DriverTypeEditModal from './DriverTypeEditModal';
import './InnovationRadarManagement.css';

function InnovationRadarManagement({ currentUser }) {
    const { user } = useAuth0();

    // Commented out all state and functionality for temporary "coming later" notice
    /*
    const [radarItems, setRadarItems] = useState([]); // Topics from main list
    const [driverTypesItems, setDriverTypesItems] = useState([]); // Drivers & Types from separate list
    const [isLoading, setIsLoading] = useState(true);
    const [isRefreshing, setIsRefreshing] = useState(false);
    const [expandedSections, setExpandedSections] = useState({
        drivers: true,
        types: true,
        topics: true
    });
    const [editingItem, setEditingItem] = useState(null);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDriverTypeModal, setShowDriverTypeModal] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    // Get minutes until next hour for flow refresh message
    const getMinutesUntilNextFlow = () => {
        const now = new Date();
        const minutes = now.getMinutes();
        const minutesUntilNextHour = 60 - minutes;
        return Math.max(minutesUntilNextHour, 1);
    };

    const fetchRadarItems = async () => {
        setIsRefreshing(true);
        try {
            // Fetch both topics and driver/types in parallel
            const [topicsData, driverTypesData] = await Promise.all([
                innovationRadarService.getInnovationRadarItems(),
                innovationRadarService.getDriverTypesItems()
            ]);
            
            setRadarItems(topicsData || []);
            setDriverTypesItems(driverTypesData || []);
            
            console.log('Loaded Innovation Radar data:');
            console.log(`   - Topics: ${topicsData?.length || 0}`);
            console.log(`   - Drivers & Types: ${driverTypesData?.length || 0}`);
        } catch (error) {
            console.error('Error fetching radar items:', error);
            toast.error('Failed to load innovation radar items');
        } finally {
            setIsRefreshing(false);
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchRadarItems();
    }, []);

    // Transform data using service helper methods
    const topics = innovationRadarService.getTopics(radarItems);
    const drivers = innovationRadarService.getDrivers(driverTypesItems);
    const types = innovationRadarService.getTypes(driverTypesItems);
    
    console.log(` Innovation Radar data breakdown:`);
    console.log(`   - Topics: ${topics.length}`);
    console.log(`   - Drivers: ${drivers.length}`);
    console.log(`   - Types: ${types.length}`);

    // Statistics - all items are published since they exist in SharePoint
    const getStats = () => {
        return {
            total: radarItems.length,
            published: radarItems.length, // All items are published
            drivers: drivers.length,
            types: types.length,
            topics: topics.length,
            publishedDrivers: drivers.length,
            publishedTypes: types.length,
            publishedTopics: topics.length
        };
    };

    const stats = getStats();

    const handleSectionToggle = (section) => {
        setExpandedSections(prev => ({
            ...prev,
            [section]: !prev[section]
        }));
    };

    const handleEdit = (item) => {
        setEditingItem(item);
        setIsCreating(false);
        
        // Show the appropriate modal based on item type
        if (item.Type === 'Driver' || item.Type === 'Type') {
            setShowDriverTypeModal(true);
            setShowEditModal(false);
        } else {
            setShowEditModal(true);
            setShowDriverTypeModal(false);
        }
    };

    const handleCreate = (contentType) => {
        let newItem;

        if (contentType === 'Topic') {
            // Create a new empty topic item
            newItem = {
                Title: '',
                ContentType: 'Topic',
                Description: '',
                Category: '',
                DriversInnovation: '',
                PrimaryBenefit: '',
                GroupColour: '',
                RankingFlag: '',
                Publish: true,
                // All SharePoint fields
                IndustrySector: '',
                TopicTheme: '',
                ImportanceLevel: '',
                UrgencyLevel: '',
                TopicLongName: '',
                Definition: ''
            };
        } else {
            // Create a new empty driver or type item
            newItem = {
                Title: '',
                Type: contentType, // 'Driver' or 'Type'
                Description: '',
                SortOrder: 0,
                CssClass: '',
                Publish: true
            };
        }

        setEditingItem(newItem);
        setIsCreating(true);
        
        // Show the appropriate modal based on content type
        if (contentType === 'Driver' || contentType === 'Type') {
            setShowDriverTypeModal(true);
            setShowEditModal(false);
        } else {
            setShowEditModal(true);
            setShowDriverTypeModal(false);
        }
    };

    const handleSave = async (itemData) => {
        try {
            const itemType = itemData.ContentType || itemData.Type || 'Topic';
            const isDriverOrType = itemType === 'Driver' || itemType === 'Type';

            if (isCreating) {
                // Creating new item
                if (isDriverOrType) {
                    await innovationRadarService.createDriverTypeItem(
                        itemData,
                        user?.name || 'Admin'
                    );
                } else {
                    await innovationRadarService.createInnovationRadarItem(
                        itemData,
                        user?.name || 'Admin'
                    );
                }

                const minutesToWait = getMinutesUntilNextFlow();
                const waitMessage = minutesToWait === 1 
                    ? `${itemType} created successfully! Changes will be live on the member portal in 1 minute when the flow runs.`
                    : `${itemType} created successfully! Changes will be live on the member portal in ${minutesToWait} minutes when the flow runs.`;
                
                toast.success(waitMessage, {
                    autoClose: 6000,
                    hideProgressBar: false
                });
            } else {
                // Updating existing item
                if (isDriverOrType) {
                    await innovationRadarService.updateDriverTypeItem(
                        itemData.ID,
                        itemData,
                        user?.name || 'Admin'
                    );
                } else {
                    await innovationRadarService.updateInnovationRadarItem(
                        itemData.ID,
                        itemData,
                        user?.name || 'Admin'
                    );
                }

                const minutesToWait = getMinutesUntilNextFlow();
                const waitMessage = minutesToWait === 1 
                    ? `${itemType} updated successfully! Changes will be live on the member portal in 1 minute when the flow runs.`
                    : `${itemType} updated successfully! Changes will be live on the member portal in ${minutesToWait} minutes when the flow runs.`;
                
                toast.success(waitMessage, {
                    autoClose: 6000,
                    hideProgressBar: false
                });
            }

            setShowEditModal(false);
            setShowDriverTypeModal(false);
            setEditingItem(null);
            setIsCreating(false);
            await fetchRadarItems();
        } catch (error) {
            console.error('Error saving radar item:', error);
            toast.error(`Failed to ${isCreating ? 'create' : 'update'} innovation radar item`);
        }
    };

    const renderContentSection = (title, items, contentType, icon, color) => {
        const sectionKey = contentType.toLowerCase() + 's';
        const isExpanded = expandedSections[sectionKey];
        
        return (
            <div key={contentType} className={`innovation-radar-section ${contentType === 'Topic' ? 'topics-section' : ''}`}>
                <div 
                    className="innovation-radar-section-header"
                    onClick={() => handleSectionToggle(sectionKey)}
                >
                    <div className="section-header-left">
                        <FontAwesomeIcon icon={icon} style={{ color }} />
                        <h3>{title}</h3>
                        <span className="section-count">
                            {items.length} topics
                        </span>
                        <button 
                            className="innovation-radar-btn primary small add-btn"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleCreate(contentType);
                            }}
                            title={`Add new ${contentType.toLowerCase()}`}
                        >
                            <FontAwesomeIcon icon={faPlus} /> 
                            Add {contentType}
                        </button>
                    </div>
                    <FontAwesomeIcon 
                        icon={isExpanded ? faChevronUp : faChevronDown} 
                        className="section-toggle"
                    />
                </div>

                {isExpanded && (
                    <div className="innovation-radar-section-content">
                        {items.length === 0 ? (
                            <div className="innovation-radar-empty">
                                <p>No {title.toLowerCase()} found.</p>
                            </div>
                        ) : (
                            <div className="innovation-radar-items-grid">
                                {items.map(item => (
                                    <div 
                                        key={item.ID} 
                                        className={`innovation-radar-item-card ${contentType === 'Topic' ? 'topic-card-compact' : ''} clickable`}
                                        onClick={() => handleEdit(item)}
                                        title="Click to edit"
                                    >
                                        <div className="item-card-header">
                                            <div className="item-title">
                                                <h4>{item.Title}</h4>
                                                {item.TopicLongName && item.TopicLongName !== item.Title && (
                                                    <span className="item-subtitle">{item.TopicLongName}</span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="item-card-body">
                                            {contentType === 'Topic' && (
                                                <div className="topic-details-compact">
                                                    <div className="topic-meta">
                                                        <span className="category-tag">{item.Category || 'No Category'}</span>
                                                        {item.RankingFlag && (
                                                            <span className="ranking-badge">#{item.RankingFlag}</span>
                                                        )}
                                                    </div>
                                                    {item.Description && (
                                                        <p className="item-description-compact">
                                                            {item.Description.length > 80 
                                                                ? `${item.Description.substring(0, 80)}...`
                                                                : item.Description
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            )}

                                            {(contentType === 'Driver' || contentType === 'Type') && (
                                                <div className="driver-type-details">
                                                    {item.Description && (
                                                        <p className="item-description">
                                                            {item.Description.length > 120 
                                                                ? `${item.Description.substring(0, 120)}...`
                                                                : item.Description
                                                            }
                                                        </p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>
        );
    };

    if (isLoading) {
        return (
            <div className="innovation-radar-loading">
                <LoadingSpinner />
            </div>
        );
    }
    */

    return (
        <div className="innovation-radar-management">
            <div className="coming-later-notice">
                <div className="notice-content">
                    <div className="notice-icon">
                        <FontAwesomeIcon icon={faHammer} size="3x" color="#6c757d" />
                    </div>
                    <h2>Innovation Radar Management</h2>
                    <h3>Coming Soon</h3>
                    <p>
                        We're currently working on the Innovation Radar Management feature. 
                        This section will allow you to manage innovation topics, drivers, and types.
                    </p>
                    <p>
                        <strong>Expected features:</strong>
                    </p>
                    <ul>
                        <li>Create and edit innovation topics</li>
                        <li>Manage drivers of innovation</li>
                        <li>Configure innovation types</li>
                        <li>Add and manage capabilities</li>
                        <li>Upload and attach media to capabilities</li>
                        <li>Organize content with categories and tags</li>
                        <li>Real-time data synchronization</li>
                    </ul>
                    <div className="notice-status">
                        <FontAwesomeIcon icon={faInfoCircle} />
                        <span>This feature will be available in a future release</span>
                    </div>
                </div>
            </div>

            <style jsx>{`
                .coming-later-notice {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    min-height: 60vh;
                    padding: 2rem;
                }

                .notice-content {
                    text-align: center;
                    max-width: 600px;
                    padding: 3rem;
                    background: #f8f9fa;
                    border-radius: 12px;
                    border: 1px solid #dee2e6;
                    box-shadow: 0 2px 8px rgba(0,0,0,0.1);
                }

                .notice-icon {
                    margin-bottom: 1.5rem;
                }

                .notice-content h2 {
                    color: #495057;
                    margin-bottom: 0.5rem;
                    font-size: 1.8rem;
                }

                .notice-content h3 {
                    color: #6c757d;
                    margin-bottom: 1.5rem;
                    font-size: 1.3rem;
                    font-weight: 400;
                }

                .notice-content p {
                    color: #6c757d;
                    line-height: 1.6;
                    margin-bottom: 1rem;
                }

                .notice-content ul {
                    text-align: left;
                    margin: 1.5rem 0;
                    padding-left: 2rem;
                }

                .notice-content li {
                    color: #6c757d;
                    margin-bottom: 0.5rem;
                    line-height: 1.5;
                }

                .notice-status {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.5rem;
                    margin-top: 2rem;
                    padding: 1rem;
                    background: #e3f2fd;
                    border-radius: 6px;
                    color: #1976d2;
                    font-size: 0.9rem;
                }
            `}</style>
        </div>
    );

    /* 
    // Original return statement - commented out for later restoration
    return (
        <div className="innovation-radar-management">
            <div className="innovation-radar-controls">
                <div className="controls-left">
                    <button 
                        className="innovation-radar-btn secondary"
                        onClick={fetchRadarItems}
                        disabled={isRefreshing}
                    >
                        <FontAwesomeIcon 
                            icon={faRotateRight} 
                            spin={isRefreshing}
                        /> 
                        {isRefreshing ? 'Refreshing...' : 'Refresh'}
                    </button>
                </div>
                <div className="controls-right">
                    <div className="innovation-radar-info">
                        <FontAwesomeIcon icon={faExclamationTriangle} />
                        Data flows to member portal every hour
                    </div>
                </div>
            </div>

            <div className="innovation-radar-stats">
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faLightbulb} />
                    </div>
                    <div className="stat-details">
                        <h3>Total Items</h3>
                        <p className="stat-number">{stats.total}</p>
                        <p className="stat-breakdown">
                            Published: {stats.published} | Draft: {stats.total - stats.published}
                        </p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faCog} />
                    </div>
                    <div className="stat-details">
                        <h3>Drivers</h3>
                        <p className="stat-number">{stats.drivers}</p>
                        <p className="stat-breakdown">
                            Published: {stats.publishedDrivers}
                        </p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faChartLine} />
                    </div>
                    <div className="stat-details">
                        <h3>Types</h3>
                        <p className="stat-number">{stats.types}</p>
                        <p className="stat-breakdown">
                            Published: {stats.publishedTypes}
                        </p>
                    </div>
                </div>
                <div className="stat-card">
                    <div className="stat-icon">
                        <FontAwesomeIcon icon={faTags} />
                    </div>
                    <div className="stat-details">
                        <h3>Topics</h3>
                        <p className="stat-number">{stats.topics}</p>
                        <p className="stat-breakdown">
                            Published: {stats.publishedTopics}
                        </p>
                    </div>
                </div>
            </div>

            <div className="innovation-radar-sections">
                {renderContentSection(
                    'Innovation Topics / Themes',
                    topics,
                    'Topic',
                    faTags,
                    '#ffc107'
                )}
                
                {renderContentSection(
                    'Types of Innovation',
                    types,
                    'Type',
                    faChartLine,
                    '#28a745'
                )}
                
                {renderContentSection(
                    'Drivers of Innovation',
                    drivers,
                    'Driver',
                    faCog,
                    '#007bff'
                )}
            </div>

            {showEditModal && editingItem && (
                <InnovationRadarEditModal
                    item={editingItem}
                    drivers={drivers}
                    isCreating={isCreating}
                    onSave={handleSave}
                    onClose={() => {
                        setShowEditModal(false);
                        setEditingItem(null);
                        setIsCreating(false);
                    }}
                />
            )}

            {showDriverTypeModal && editingItem && (
                <DriverTypeEditModal
                    item={editingItem}
                    isCreating={isCreating}
                    onSave={handleSave}
                    onClose={() => {
                        setShowDriverTypeModal(false);
                        setEditingItem(null);
                        setIsCreating(false);
                    }}
                />
            )}
        </div>
    );
    */
}

export default InnovationRadarManagement; 